import React, { createContext, useContext, useReducer, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  createInitialState,
  downloadQueueReducer,
  selectActive,
  selectNextQueued,
  selectPendingCount,
  selectErrorCount,
  selectPersistedIds,
  selectItem,
} from '../lib/offline/downloadQueueReducer';
import {
  downloadEpisode as storeDownloadEpisode,
  removeEpisode as storeRemoveEpisode,
  getDownloadedIds,
} from '../lib/offline/offlineStore';

const STORAGE_KEY = 'archives-download-queue-v1';
const PROGRESS_THROTTLE_MS = 250;

const DownloadManagerContext = createContext(null);

/**
 * Sequential download-queue provider: at most one episode downloads at a
 * time, everything else waits in `items`. Replaces the old single-slot
 * offline-download provider/hook pair.
 *
 * `episodes` is the full podcasts list (may start `[]` while it loads) — it
 * is only used to resolve a previously-persisted queue of ids back into full
 * episode objects once it becomes available.
 */
export function DownloadManagerProvider({ episodes, children }) {
  const [state, dispatch] = useReducer(downloadQueueReducer, undefined, createInitialState);
  const [downloadedIds, setDownloadedIds] = useState(() => getDownloadedIds());

  // One-shot latch: the restore effect below should only ever act once, the
  // first time `episodes` is non-empty. Not reset anywhere (including
  // cleanup) — it is not a "mounted" flag, it is a "did the one-time restore
  // already happen" latch, and it must stay true for the life of this
  // provider instance (including across StrictMode's dev-only synthetic
  // double-invoke of the initial mount's effects).
  const restoredRef = useRef(false);

  // AbortController and progress-throttle bookkeeping, keyed by episode id.
  // Refs (not state) per the brief: they must not cause re-renders.
  const controllersRef = useRef({});
  const lastProgressRef = useRef({});

  // Restore once episodes are available: map stored ids back to episode
  // objects, dropping ids that no longer exist or are already downloaded.
  useEffect(() => {
    if (restoredRef.current) return;
    if (!episodes || episodes.length === 0) return;
    restoredRef.current = true;

    let storedIds;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      storedIds = raw ? JSON.parse(raw) : [];
    } catch (_) {
      storedIds = [];
    }
    if (!Array.isArray(storedIds) || storedIds.length === 0) return;

    const toRestore = storedIds
      .map((id) => episodes.find((ep) => ep.id === id))
      .filter((ep) => ep && !downloadedIds.includes(ep.id));

    if (toRestore.length > 0) {
      dispatch({ type: 'RESTORE', episodes: toRestore });
    }
  }, [episodes, downloadedIds]);

  // Persist the queue (queued + downloading ids) after every state change.
  // Gated on `episodes` having loaded at least once so this never overwrites
  // a previous-session seed with an empty array before the restore effect
  // above (which waits for `episodes`) has had a chance to read it.
  useEffect(() => {
    if (!episodes || episodes.length === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectPersistedIds(state)));
    } catch (_) {
      // Storage unavailable/full — persistence is best-effort.
    }
  }, [state, episodes]);

  // Driver: whenever nothing is downloading and something is queued, start
  // it. Re-derived from `state` on every change (idempotent by construction:
  // once START is dispatched, `selectActive` is no longer null, so this
  // branch does not fire again for the same item, StrictMode double-invoke
  // or not).
  useEffect(() => {
    if (selectActive(state)) return;
    const next = selectNextQueued(state);
    if (!next) return;

    const { id, episode } = next;
    dispatch({ type: 'START', id });

    const controller = new AbortController();
    controllersRef.current[id] = controller;
    lastProgressRef.current[id] = { pct: -1, time: 0 };

    const onProgress = ({ ratio }) => {
      const pct = ratio != null ? ratio * 100 : 0;
      const last = lastProgressRef.current[id] || { pct: -1, time: 0 };
      const now = Date.now();
      const flooredChanged = Math.floor(pct) !== Math.floor(last.pct);
      const elapsed = now - last.time >= PROGRESS_THROTTLE_MS;
      // A ratio of 1 (100%) always gets through, even mid-throttle-window,
      // so the UI never freezes short of "complete".
      if (pct >= 100 || (flooredChanged && elapsed)) {
        lastProgressRef.current[id] = { pct, time: now };
        dispatch({ type: 'PROGRESS', id, pct });
      }
    };

    // Only clear the id's ref slots if they still point at THIS invocation's
    // controller. Guards a same-id-restart race: cancel() + an immediate
    // re-enqueue starts a second, distinct attempt for the same id before
    // this (superseded) attempt's promise has settled; when it settles late,
    // it must not delete the new attempt's controller/progress bookkeeping
    // out from under it.
    const clearRefsIfCurrent = () => {
      if (controllersRef.current[id] === controller) {
        delete controllersRef.current[id];
        delete lastProgressRef.current[id];
      }
    };

    storeDownloadEpisode(episode, onProgress, controller.signal)
      .then(() => {
        clearRefsIfCurrent();
        dispatch({ type: 'COMPLETE', id });
        setDownloadedIds(getDownloadedIds());
      })
      .catch((err) => {
        clearRefsIfCurrent();
        if (err && err.name === 'AbortError') return; // CANCEL already removed the item.
        dispatch({ type: 'FAIL', id, message: (err && err.message) || 'Download failed' });
      });
  }, [state]);

  const enqueue = useCallback((episode) => {
    if (!episode || downloadedIds.includes(episode.id)) return;
    dispatch({ type: 'ENQUEUE', episode });
  }, [downloadedIds]);

  const cancel = useCallback((id) => {
    const controller = controllersRef.current[id];
    if (controller) {
      controller.abort();
      // Identity check for consistency with the driver's own cleanup above —
      // a no-op today (this read-then-delete is synchronous, nothing else
      // can race it), but keeps both cleanup sites expressing the same
      // invariant: never delete a ref slot that isn't still "ours".
      if (controllersRef.current[id] === controller) {
        delete controllersRef.current[id];
        delete lastProgressRef.current[id];
      }
    }
    dispatch({ type: 'CANCEL', id });
  }, []);

  const retry = useCallback((id) => {
    dispatch({ type: 'RETRY', id });
  }, []);

  const removeEpisode = useCallback(async (id) => {
    await storeRemoveEpisode(id);
    // Purge any lingering reducer item for this id too (typically a 'done'
    // one from a prior completed download) — not just downloadedIds.
    // statusFor checks selectItem before downloadedIds, so a lingering item
    // would keep reporting its old status forever, and ENQUEUE dedups by id
    // across every status, so a later enqueue would silently do nothing.
    // CANCEL is reused here purely as an unconditional filter-out by id
    // (there is no in-flight download to abort at this point since an active
    // item is never simultaneously in downloadedIds); the reducer itself is
    // untouched.
    dispatch({ type: 'CANCEL', id });
    setDownloadedIds(getDownloadedIds());
  }, []);

  const statusFor = useCallback((id) => {
    const item = selectItem(state, id);
    if (item) {
      return { status: item.status, progress: item.progress, error: item.error };
    }
    if (downloadedIds.includes(id)) {
      return { status: 'done', progress: 100, error: null };
    }
    return { status: 'idle', progress: null, error: null };
  }, [state, downloadedIds]);

  const value = useMemo(() => ({
    // Note: a completed item stays in `items` as 'done' for the life of the
    // session (only removeEpisode/CANCEL purge it) — consumers that want
    // just the active queue should filter by status themselves.
    items: state.items,
    activeItem: selectActive(state),
    pendingCount: selectPendingCount(state),
    errorCount: selectErrorCount(state),
    downloadedIds,
    enqueue,
    cancel,
    retry,
    removeEpisode,
    statusFor,
  }), [state, downloadedIds, enqueue, cancel, retry, removeEpisode, statusFor]);

  return (
    <DownloadManagerContext.Provider value={value}>
      {children}
    </DownloadManagerContext.Provider>
  );
}

export function useDownloads() {
  const ctx = useContext(DownloadManagerContext);
  if (!ctx) throw new Error('useDownloads must be used within DownloadManagerProvider');
  return ctx;
}
